import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { activityCenterActions } from '../store/slices/activityCenterSlice';
import { TASK_STATUS } from '../constants/activityCenter';
import { useSignalRManager } from './useSignalRManager';

/**
 * Activity Center Integration Hook with SignalR
 * Provides a generic way to integrate SignalR events with the Activity Center
 */
export const useActivityCenterSignalR = () => {
  const dispatch = useDispatch();
  const { subscribeTopic, unsubscribeTopic, isConnected } = useSignalRManager();

  const addActivityItem = useCallback((itemConfig) => {
    const {
      id,
      title,
      description,
      type,
      payload = null,
      status = TASK_STATUS.QUEUED,
      progress = 0
    } = itemConfig;

    const task = {
      id: id || Date.now().toString(),
      title,
      description,
      type,
      payload,
      status,
      progress,
      createdAt: new Date().toISOString()
    };

    dispatch(activityCenterActions.addTask(task));
    return task.id;
  }, [dispatch]);

  const updateActivityItem = useCallback((id, updates) => {
    dispatch(activityCenterActions.updateTaskStatus({
      id,
      ...updates
    }));
  }, [dispatch]);

  const completeActivityItem = useCallback((id, result) => {
    dispatch(activityCenterActions.completeTask({
      id,
      result
    }));
  }, [dispatch]);

  const failActivityItem = useCallback((id, error) => {
    dispatch(activityCenterActions.failTask({
      id,
      error
    }));
  }, [dispatch]);

  const subscribeToActivityTopic = useCallback(async (topicConfig) => {
    const {
      name,
      serverMethod = null,
      serverMethodParams = null,
      eventHandlers = []
    } = topicConfig;

    const enhancedEvents = eventHandlers.map((eventHandler) => {
      const { eventName, handler, clientFilter = null } = eventHandler;

      return {
        eventName,
        handler: (...args) => {
          if (clientFilter && !clientFilter(...args)) {
            return;
          }

          if (handler) {
            handler(...args);
          }
        }
      };
    });

    const signalRTopicConfig = {
      name,
      eventHandlers: enhancedEvents,
      serverMethod,
      serverMethodParams
    };

    return subscribeTopic(signalRTopicConfig);
  }, [subscribeTopic]);

  const unsubscribeFromActivityTopic = useCallback((topicName, serverUnsubscribeMethod = null, serverUnsubscribeParams = null) => unsubscribeTopic(topicName, serverUnsubscribeMethod, serverUnsubscribeParams), [unsubscribeTopic]);

  return {
    addActivityItem,
    updateActivityItem,
    completeActivityItem,
    failActivityItem,
    subscribeToActivityTopic,
    unsubscribeFromActivityTopic,
    isConnected
  };
};

export default useActivityCenterSignalR;
